import React, {useMemo} from 'react';
import {Link} from 'react-router-dom';

import {UserLink} from 'src/components/links';
import {MainColumnCell, MainColumnHeader, getPaginatedRowNumber} from 'src/components/main-column';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {DraftStatus} from 'src/openapi/admin/models/DraftStatus';
import {SetUpStep} from 'src/openapi/admin/models/SetUpStep';

import {
    Cell,
    type CellOptions,
    Column,
    DataTable,
    HeaderCellOptions,
    type RowsParams,
    TableHeader,
} from '@pb/kit-component-data-table-v2';
import DiffTime from '@pb/kit-component-diff-time';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Tooltip} from '@pb/kit-component-popups';

import {COLUMNS, COLUMNS_DATA, HEADERS, SECTION_NAME, SORT_FIELDS} from '../constants';
import {formatBoolean, formatEnumValue} from '../utils/format';
import {isPostponeWarning} from '../utils/is-postpone-warning';

import {SsoAccountsPagination} from './sso-accounts-pagination';
import {SsoAccountsSortHeader} from './sso-accounts-sort-header';
import {SsoTimestampCell} from './sso-timestamp-cell';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {SsoDraft} from 'src/openapi/admin/models/SsoDraft';


type Props = {
    rows: SsoDraft[],
    totalCount: number,
    pagination: Pagination,
    stickyReferenceElement?: HTMLElement,
};

type AccountNameColumnValue = {
    accountId: number,
    accountName: string,
};

type CustomerAdminColumnValue = {
    userId: number,
    caption: string,
};

export const SsoAccountsTable = ({
    rows,
    totalCount,
    pagination,
    stickyReferenceElement,
}: Props) => {
    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();

    const renderedRows = useMemo(() => rows.map(draft => ({
        id: draft.id,
        columnValues: {
            accountName: {
                accountId: draft.accountId,
                accountName: draft.accountName || `${draft.accountId}`,
            },
            status: formatEnumValue(draft.status),
            currentStep: formatEnumValue(draft.currentStep),
            ssoEnabled: formatBoolean(draft.ssoEnabled),
            ssoMandatory: formatBoolean(draft.ssoMandatory),
            created: draft.createdAt,
            finished: draft.finishedAt,
            customerAdmin: {
                userId: draft.createdBy,
                caption: draft.creatorEmail || `${draft.createdBy}`,
            },
            lastUpdate: draft.updatedAt,
            statusRaw: draft.status,
            latestStep: draft.latestStep,
        },
    })), [rows]);

    const maxRowNumber = getPaginatedRowNumber({...pagination, index: renderedRows.length});

    return (
        <DataTable
            id="sso-accounts-table"
            data={{
                rows: renderedRows, columns: COLUMNS_DATA.map((column: Column) => ({
                    ...column,
                    width: getColumnWidthByColumnId(column.columnId),
                })),
            }}
            stickyReferenceElement={stickyReferenceElement}
            onStopResize={onStopResize}
            headers={({column}: {column: Column}, cellOptions: HeaderCellOptions) => {
                const sortField = SORT_FIELDS[column.columnId];

                switch (column.columnId) {
                    case COLUMNS.ACCOUNT_NAME:
                        return (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <MainColumnHeader
                                    selection={<div/>}
                                    maxRowNumber={maxRowNumber}>
                                    <strong>{HEADERS[column.columnId]}</strong>
                                </MainColumnHeader>
                            </Cell>
                        );
                    default:
                        return (
                            <Cell
                                {...cellOptions}
                                key={`${column.columnId}`}
                                column={column}
                                resizable={true}>
                                <TableHeader indent="none" className="search-header">
                                    {sortField ? (
                                        <SsoAccountsSortHeader
                                            columnId={column.columnId}
                                            sortField={sortField}
                                        />
                                    ) : (
                                        <strong>{HEADERS[column.columnId]}</strong>
                                    )}
                                </TableHeader>
                            </Cell>
                        );
                }
            }}
            renderRows={(rowsParams: RowsParams, cellOptions: CellOptions) => {
                const {rowIndex, column: {columnId}} = rowsParams;
                const value = rowsParams.row.columnValues[columnId];
                const column = rowsParams.column;
                const rowId = +rowsParams.row.id;
                const cellKey = `${rowId}-${columnId}`;
                const rowNumber = getPaginatedRowNumber({
                    ...pagination,
                    index: rowIndex + 1,
                });

                switch (columnId) {
                    case COLUMNS.ACCOUNT_NAME:
                        const account = value as AccountNameColumnValue;
                        return (
                            <MainColumnCell
                                rowId={rowId}
                                key={cellKey}
                                selection={<div/>}
                                rowNumber={rowNumber}
                                maxRowNumber={maxRowNumber}
                                cellOptions={{...cellOptions, column}}>
                                <strong>
                                    <Link to={`/sso-accounts/${account.accountId}`}>
                                        <EllipsisTextHint title={account.accountName}>
                                            {account.accountName}
                                        </EllipsisTextHint>
                                    </Link>
                                </strong>
                            </MainColumnCell>
                        );
                    case COLUMNS.CREATED:
                    case COLUMNS.FINISHED:
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <SsoTimestampCell timestamp={value as string | undefined}/>
                            </Cell>
                        );
                    case COLUMNS.CUSTOMER_ADMIN:
                        const customerAdmin = value as CustomerAdminColumnValue;
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <UserLink
                                    userId={customerAdmin.userId}
                                    caption={customerAdmin.caption}
                                    place={SECTION_NAME}
                                />
                            </Cell>
                        );
                    case COLUMNS.LAST_UPDATE:
                        const updatedAt = value as string | undefined;
                        if (!updatedAt) {
                            return (
                                <Cell {...cellOptions} key={cellKey} column={column} className="search-result-cell">
                                    --
                                </Cell>
                            );
                        }

                        const showWarning = isPostponeWarning({
                            updatedAt,
                            latestStep: rowsParams.row.columnValues.latestStep as SetUpStep,
                            status: rowsParams.row.columnValues.statusRaw as DraftStatus,
                        });

                        return (
                            <Cell {...cellOptions} key={cellKey} column={column} className="search-result-cell">
                                <Group alignItems="center" gap="xs">
                                    <DiffTime createdDate={updatedAt} fullUnits={true}/>
                                    {showWarning ? (
                                        <Tooltip
                                            color="light"
                                            placement="top-start"
                                            anchor={({anchorRef, getAnchorProps}) => (
                                                <div ref={anchorRef} {...getAnchorProps()}>
                                                    <Icon type={Icons.WARNING}/>
                                                </div>
                                            )}>
                                            If the draft is not finalized, it will be automatically finalized 28 days
                                            after the last update date.
                                        </Tooltip>
                                    ) : null}
                                </Group>
                            </Cell>
                        );
                    default:
                        const displayValue = String(value);
                        return (
                            <Cell
                                {...cellOptions}
                                key={cellKey}
                                column={column}
                                className="search-result-cell">
                                <EllipsisTextHint title={displayValue}>
                                    {displayValue}
                                </EllipsisTextHint>
                            </Cell>
                        );
                }
            }}
            paginationStripe={<SsoAccountsPagination totalCount={totalCount}/>}
        />
    );
};
